<template>
  <div class="g-page">
    <div class="content-container">
      <!-- HEADER -->
      <div class="header-row">
        <h2 class="page-title">
          <b-icon icon="graph-up-arrow" class="title-icon"></b-icon>
          Foyda-Zarar Hisoboti
        </h2>
        <div class="header-actions">
          <button class="mode-btn" :class="{ active: mode === 'sales'    }" @click="setMode('sales')">
            <b-icon icon="receipt"></b-icon> Savdolar bo'yicha
          </button>
          <button class="mode-btn" :class="{ active: mode === 'employee' }" @click="setMode('employee')">
            <b-icon icon="people-fill"></b-icon> Xodimlar bo'yicha
          </button>
          <button class="mode-btn" :class="{ active: mode === 'pnl' }" @click="setMode('pnl')"
            title="Mobil Foyda-Zarar bilan AYNAN bir xil (profit-loss API) — kunlik">
            <b-icon icon="calendar3"></b-icon> Kunlik (P&L)
          </button>
          <button class="anomaly-btn" @click="openAnomalies()"
            title="Tannarxi sotish narxidan baland (shishib ketgan) qatorlarni topish">
            <b-icon icon="exclamation-triangle-fill"></b-icon> Shubhali tannarx
          </button>
          <button class="recalc-btn" @click="openRecalc()"
            title="Tannarxi 0 bo'lib sotilgan savdolarning shishgan foydasini tuzatish">
            <b-icon icon="wrench-adjustable"></b-icon> Tannarx 0 foydani tuzatish
          </button>
        </div>
      </div>

      <!-- FILTER -->
      <div class="filter-panel">
        <div class="filter-grid">
          <div class="filter-cell">
            <label>Sanadan</label>
            <input type="date" v-model="filters.date1" class="form-input" />
          </div>
          <div class="filter-cell">
            <label>Sanagacha</label>
            <input type="date" v-model="filters.date2" class="form-input" />
          </div>
          <div class="filter-cell">
            <label>Sklad</label>
            <select v-model="filters.sklad_id" class="form-input">
              <option value="">Joriy sklad</option>
              <option value="all">Barcha skladlar</option>
              <option v-for="s in sklads" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>
          <div v-if="mode === 'sales'" class="filter-cell">
            <label>Tartiblash</label>
            <select v-model="filters.order_by" class="form-input">
              <option value="date">Sana (yangi-eski)</option>
              <option value="profit">Foyda (ko'p-kam)</option>
              <option value="profit_pct">Foyda %</option>
              <option value="net_profit">Sof foyda</option>
              <option value="revenue">Tushum (ko'p-kam)</option>
            </select>
          </div>
          <div class="filter-cell">
            <label>&nbsp;</label>
            <div class="action-row">
              <button class="btn-primary" @click="reload()" :disabled="loading">
                <b-icon icon="search"></b-icon> {{ loading ? 'Yuklanmoqda...' : 'Hisoblash' }}
              </button>
              <button class="btn-secondary" :class="{ active: activeRange === 'today' }" @click="quickRange('today')">Bugun</button>
              <button class="btn-secondary" :class="{ active: activeRange === 'week' }" @click="quickRange('week')">Hafta</button>
              <button class="btn-secondary" :class="{ active: activeRange === 'month' }" @click="quickRange('month')">Oy</button>
            </div>
          </div>
        </div>
      </div>

      <!-- SUMMARY CARDS -->
      <div v-if="totals && mode !== 'pnl'" class="summary-row">
        <div class="summary-card primary">
          <div class="card-label">Tushum</div>
          <div class="card-value">{{ formatMoney(totals.revenue || totals.total_revenue) }}</div>
          <div class="card-sub">{{ totals.sales_count || total }} ta savdo</div>
        </div>
        <div class="summary-card warning">
          <div class="card-label">Tannarx</div>
          <div class="card-value">{{ formatMoney(totals.product_cost) }}</div>
          <div class="card-sub">mahsulotlar</div>
        </div>
        <div class="summary-card success">
          <div class="card-label">Brutto foyda</div>
          <div class="card-value">{{ formatMoney(totals.gross_profit) }}</div>
          <div class="card-sub">tushum − tannarx</div>
        </div>
        <div class="summary-card info">
          <div class="card-label">KPI ulushi</div>
          <div class="card-value">{{ formatMoney(totals.kpi_share) }}</div>
          <div class="card-sub">xodimlarga</div>
        </div>
        <div class="summary-card purple">
          <div class="card-label">Usta ulushi</div>
          <div class="card-value">{{ formatMoney(totals.master_share) }}</div>
          <div class="card-sub">ustalar bonus</div>
        </div>
        <div class="summary-card" :class="(totals.net_profit || 0) >= 0 ? 'success-strong' : 'danger'">
          <div class="card-label">Sof foyda</div>
          <div class="card-value">{{ formatMoney(totals.net_profit) }}</div>
          <div class="card-sub">brutto − KPI − usta</div>
        </div>
      </div>

      <!-- SALES TABLE -->
      <div v-if="mode === 'sales'" class="table-card">
        <div class="table-wrapper">
          <table class="custom-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Sana</th>
                <th>Xodim</th>
                <th>Mijoz</th>
                <th class="num">Tushum</th>
                <th class="num">Tannarx</th>
                <th class="num">Foyda</th>
                <th class="num">%</th>
                <th class="num">KPI</th>
                <th class="num">Usta</th>
                <th class="num">Sof foyda</th>
                <th>Status</th>
                <th style="width:80px;text-align:center">Detalar</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, i) in sales" :key="r.sale_id" :class="rowProfitClass(r)">
                <td>{{ offset + i + 1 }}</td>
                <td class="muted">{{ formatDate(r.created_at) }}</td>
                <td class="name-cell">
                  <div class="user-name">{{ r.user.first_name }} {{ r.user.last_name }}</div>
                  <div class="user-username">@{{ r.user.username }}</div>
                </td>
                <td>{{ r.kontragent ? r.kontragent.name : '—' }}</td>
                <td class="num bold money">{{ formatMoney(r.revenue) }}</td>
                <td class="num muted">{{ formatMoney(r.product_cost) }}</td>
                <td class="num">
                  <span :class="profitClass(r.gross_profit)">{{ formatMoney(r.gross_profit) }}</span>
                </td>
                <td class="num">
                  <span :class="profitPctClass(r.profit_pct)">{{ r.profit_pct }}%</span>
                </td>
                <td class="num kpi-cell">{{ formatMoney(r.kpi_share) }}</td>
                <td class="num master-cell">
                  <span v-if="r.master_share > 0">{{ formatMoney(r.master_share) }}</span>
                  <span v-else class="muted">—</span>
                </td>
                <td class="num bold">
                  <span :class="profitClass(r.net_profit)">{{ formatMoney(r.net_profit) }}</span>
                </td>
                <td>
                  <span class="status-badge" :class="'status-' + r.status">
                    {{ statusLabel(r.status) }}
                  </span>
                </td>
                <td style="text-align:center">
                  <button class="icon-btn" @click="openDetail(r)" title="To'liq">
                    <b-icon icon="eye-fill"></b-icon>
                  </button>
                </td>
              </tr>
              <tr v-if="sales.length === 0 && !loading">
                <td colspan="13" class="empty-row">Ma'lumot topilmadi.</td>
              </tr>
              <tr v-if="loading">
                <td colspan="13" class="loading-row"><div class="spinner"></div> Yuklanmoqda...</td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Pagination -->
        <div class="pagination-bar" v-if="total > 0">
          <span>Ko'rsatildi: <b>{{ sales.length }}</b> / <b>{{ total }}</b></span>
          <button v-if="hasMore && !loading" class="load-more-btn" @click="loadMore()">
            Yana {{ Math.min(LIMIT, total - sales.length) }} ta yuklash
          </button>
          <span v-if="!hasMore && sales.length > 0" class="all-loaded">Hammasi yuklandi ✓</span>
        </div>
      </div>

      <!-- EMPLOYEE TABLE -->
      <div v-if="mode === 'employee'" class="table-card">
        <div class="table-wrapper">
          <table class="custom-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Xodim</th>
                <th class="num">Savdolar</th>
                <th class="num">Tushum</th>
                <th class="num">Tannarx</th>
                <th class="num">Brutto foyda</th>
                <th class="num">%</th>
                <th class="num">KPI %</th>
                <th class="num">KPI summa</th>
                <th class="num">Usta</th>
                <th class="num">Sof foyda</th>
                <th style="width:90px;text-align:center">Tahlil</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in employees" :key="r.user_id" class="clickable-row" @click="openEmployee(r)" title="Savdolari va mahsulot tahlili">
                <td>
                  <span class="rank-badge" :class="rankBadgeClass(r.rank)">{{ r.rank }}</span>
                </td>
                <td class="name-cell">
                  <div class="user-name">{{ r.first_name }} {{ r.last_name }}</div>
                  <div class="user-username">@{{ r.username }}</div>
                </td>
                <td class="num"><span class="count-badge">{{ r.sales_count }}</span></td>
                <td class="num bold money">{{ formatMoney(r.total_revenue) }}</td>
                <td class="num muted">{{ formatMoney(r.product_cost) }}</td>
                <td class="num">
                  <span :class="profitClass(r.gross_profit)">{{ formatMoney(r.gross_profit) }}</span>
                </td>
                <td class="num">
                  <span :class="profitPctClass(r.profit_pct)">{{ r.profit_pct }}%</span>
                </td>
                <td class="num"><span class="kpi-pct">{{ r.kpi_percent }}%</span></td>
                <td class="num kpi-cell">{{ formatMoney(r.kpi_share) }}</td>
                <td class="num master-cell">
                  <span v-if="r.master_share > 0">{{ formatMoney(r.master_share) }}</span>
                  <span v-else class="muted">—</span>
                </td>
                <td class="num bold">
                  <span :class="profitClass(r.net_profit)">{{ formatMoney(r.net_profit) }}</span>
                </td>
                <td style="text-align:center">
                  <button class="icon-btn" @click.stop="openEmployee(r)" title="Savdolari va mahsulot tahlili">
                    <b-icon icon="search"></b-icon>
                  </button>
                </td>
              </tr>
              <tr v-if="employees.length === 0 && !loading">
                <td colspan="12" class="empty-row">Ma'lumot topilmadi.</td>
              </tr>
              <tr v-if="loading">
                <td colspan="12" class="loading-row"><div class="spinner"></div> Yuklanmoqda...</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ═══════ KUNLIK (P&L) — mobil profit-loss API bilan AYNAN bir xil ═══════ -->
      <div v-if="mode === 'pnl'">
        <div v-if="pnl" class="summary-row">
          <div class="summary-card primary">
            <div class="card-label">Tushum</div>
            <div class="card-value">{{ formatMoney(pnl.revenue.total) }}</div>
            <div class="card-sub">{{ pnl.cogs.sales_count }} ta savdo</div>
          </div>
          <div class="summary-card warning">
            <div class="card-label">Tannarx (COGS)</div>
            <div class="card-value">{{ formatMoney(pnl.cogs.product_net) }}</div>
            <div class="card-sub">service_sale + chiqim</div>
          </div>
          <div class="summary-card" :class="(pnl.gross_profit.total || 0) >= 0 ? 'success' : 'danger'">
            <div class="card-label">Yalpi foyda</div>
            <div class="card-value">{{ formatMoney(pnl.gross_profit.total) }}</div>
            <div class="card-sub">marja {{ pnl.gross_profit.margin_percent }}%</div>
          </div>
          <div class="summary-card purple">
            <div class="card-label">Chiqimlar</div>
            <div class="card-value">{{ formatMoney(pnl.expenses.total) }}</div>
            <div class="card-sub">xarajat + oylik + usta</div>
          </div>
          <div class="summary-card" :class="(pnl.net_profit.value || 0) >= 0 ? 'success-strong' : 'danger'">
            <div class="card-label">Sof foyda</div>
            <div class="card-value">{{ formatMoney(pnl.net_profit.value) }}</div>
            <div class="card-sub">marja {{ pnl.net_profit.margin_percent }}%</div>
          </div>
        </div>

        <!-- Hujjat turlari bo'yicha taqsimot — "Savdolar foydasi" jadvali FAQAT
             savdo (service_sale) ni ko'rsatadi. Agar bu yerda "Чиқим" qatori bo'lsa,
             ikki blok orasidagi farq aynan shundan. -->
        <div v-if="pnl && pnl.by_doc_type && pnl.by_doc_type.length" class="table-card">
          <div class="pnl-note">
            🧾 <b>Hujjat turlari bo'yicha</b> — pastdagi "Savdolar" jadvali faqat
            <i>Savdo qilindi</i> qatorini qamraydi. Boshqa qatorlar farqni tushuntiradi.
          </div>
          <div class="table-wrapper">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Hujjat turi</th>
                  <th class="num">Hujjat</th>
                  <th class="num">Tushum</th>
                  <th class="num">Tannarx</th>
                  <th class="num">Foyda</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in pnl.by_doc_type" :key="d.doc_type">
                  <td class="bold">{{ d.label }}</td>
                  <td class="num muted">{{ d.docs_count }}</td>
                  <td class="num money">{{ formatMoney(d.revenue) }}</td>
                  <td class="num muted">{{ formatMoney(d.cost) }}</td>
                  <td class="num bold"><span :class="profitClass(d.profit)">{{ formatMoney(d.profit) }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="table-card">
          <div class="pnl-note">
            ℹ️ Bu ko'rinish mobil "Foyda-Zarar" bilan <b>AYNAN bir xil API</b> (<code>profit-loss</code>) —
            service_sale + chiqim + qaytarish. Tannarx <b>partiya qabul qilingandagi kurs</b> bilan
            so'mga aylantiriladi (joriy kurs emas), shuning uchun sana filtrini o'zgartirsangiz
            o'tgan davr raqamlari o'zgarmaydi. Kunga bossangiz — o'sha kun bo'yicha "Shubhali tannarx" ochiladi.
          </div>
          <div class="table-wrapper">
            <table class="custom-table">
              <thead>
                <tr>
                  <th>Sana</th>
                  <th class="num">Tushum</th>
                  <th class="num">Tannarx</th>
                  <th class="num">Yalpi foyda</th>
                  <th class="num">Chiqim</th>
                  <th class="num">Sof foyda</th>
                  <th style="width:90px;text-align:center">Tekshir</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="d in (pnl ? pnl.by_period : [])" :key="d.period"
                    :class="d.net_profit < 0 ? 'row-loss' : 'row-profit'" style="cursor:pointer"
                    @click="openDayAnomalies(d.period)" title="Shu kunning shubhali tannarxlarini ochish">
                  <td class="bold">{{ d.period }}</td>
                  <td class="num money">{{ formatMoney(d.revenue) }}</td>
                  <td class="num muted">{{ formatMoney(d.cogs) }}</td>
                  <td class="num bold"><span :class="profitClass(d.gross_profit)">{{ formatMoney(d.gross_profit) }}</span></td>
                  <td class="num muted">{{ formatMoney(d.total_expense) }}</td>
                  <td class="num bold"><span :class="profitClass(d.net_profit)">{{ formatMoney(d.net_profit) }}</span></td>
                  <td style="text-align:center">
                    <button class="icon-btn" @click.stop="openDayAnomalies(d.period)" title="Shubhali tannarx">
                      <b-icon icon="exclamation-triangle"></b-icon>
                    </button>
                  </td>
                </tr>
                <tr v-if="pnl && pnl.by_period.length === 0 && !loading">
                  <td colspan="7" class="empty-row">Ma'lumot topilmadi.</td>
                </tr>
                <tr v-if="loading">
                  <td colspan="7" class="loading-row"><div class="spinner"></div> Yuklanmoqda...</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- DETAIL MODAL -->
    <b-modal id="modal-detail" hide-footer size="xl" no-close-on-backdrop modal-class="modern-modal">
      <template #modal-title>
        <span v-if="detail">
          <b-icon icon="receipt"></b-icon>
          Savdo #{{ detail.sale.number }} — to'liq tafsilot
        </span>
      </template>

      <div v-if="detail" class="detail-content">
        <!-- INFO STRIP -->
        <div class="info-strip">
          <div class="info-cell">
            <span class="info-label">Sana</span>
            <span class="info-val">{{ formatDate(detail.sale.created_at) }}</span>
          </div>
          <div class="info-cell">
            <span class="info-label">Xodim</span>
            <span class="info-val">{{ detail.user.first_name }} {{ detail.user.last_name }}</span>
          </div>
          <div class="info-cell">
            <span class="info-label">Mijoz</span>
            <span class="info-val">{{ detail.kontragent ? detail.kontragent.name : '—' }}</span>
          </div>
          <div class="info-cell">
            <span class="info-label">Usta</span>
            <span class="info-val">{{ detail.master ? detail.master.name : '—' }}</span>
          </div>
          <div class="info-cell">
            <span class="info-label">To'lov</span>
            <span class="info-val">{{ detail.pay_type.name }}</span>
          </div>
        </div>

        <!-- SUMMARY CARDS -->
        <div class="detail-summary-row">
          <div class="d-card cyan">
            <span class="d-label">Mahsulot tushumi</span>
            <span class="d-val">{{ formatMoney(detail.summary.product_revenue) }}</span>
          </div>
          <div class="d-card orange">
            <span class="d-label">Mahsulot tannarx</span>
            <span class="d-val">{{ formatMoney(detail.summary.product_cost) }}</span>
          </div>
          <div class="d-card purple">
            <span class="d-label">Xizmat tushumi</span>
            <span class="d-val">{{ formatMoney(detail.summary.service_revenue) }}</span>
          </div>
          <div class="d-card green">
            <span class="d-label">Brutto foyda</span>
            <span class="d-val">{{ formatMoney(detail.summary.gross_profit) }}</span>
          </div>
          <div class="d-card blue">
            <span class="d-label">KPI ({{ detail.user.percent }}%)</span>
            <span class="d-val">{{ formatMoney(detail.summary.kpi_share) }}</span>
          </div>
          <div class="d-card pink">
            <span class="d-label">Usta ulushi</span>
            <span class="d-val">{{ formatMoney(detail.summary.master_share) }}</span>
          </div>
          <div class="d-card" :class="detail.summary.net_profit >= 0 ? 'success-strong' : 'danger'">
            <span class="d-label">SOF FOYDA</span>
            <span class="d-val big">{{ formatMoney(detail.summary.net_profit) }}</span>
          </div>
        </div>

        <!-- ITEMS -->
        <h5 class="section-title">📦 Mahsulotlar</h5>
        <div class="items-table-wrapper">
          <table class="custom-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Mahsulot</th>
                <th class="num">Soni</th>
                <th class="num">Tannarx</th>
                <th class="num">Sotilgan</th>
                <th class="num">Birlik foyda</th>
                <th class="num">Jami tushum</th>
                <th class="num">Jami tannarx</th>
                <th class="num">Jami foyda</th>
                <th class="num">%</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(it, i) in detail.product_items" :key="i" :class="rowProfitClass(it)">
                <td>{{ i + 1 }}</td>
                <td class="name-cell">
                  <div class="user-name">{{ it.product_name }}</div>
                  <div class="user-username">{{ it.extra_name }} · #{{ it.product_number }}</div>
                </td>
                <td class="num">{{ it.count }} {{ it.unit_name }}</td>
                <td class="num muted">{{ formatMoney(it.body_price) }}</td>
                <td class="num">{{ formatMoney(it.buy_price) }}</td>
                <td class="num">
                  <span :class="profitClass(it.profit_price)">{{ formatMoney(it.profit_price) }}</span>
                </td>
                <td class="num bold">{{ formatMoney(it.line_revenue) }}</td>
                <td class="num muted">{{ formatMoney(it.line_cost) }}</td>
                <td class="num bold">
                  <span :class="profitClass(it.line_profit)">{{ formatMoney(it.line_profit) }}</span>
                </td>
                <td class="num">
                  <span :class="profitPctClass(it.profit_pct)">{{ it.profit_pct }}%</span>
                </td>
              </tr>
              <tr v-if="detail.product_items.length === 0">
                <td colspan="10" class="empty-row">Mahsulotlar yo'q (xizmat-only savdo)</td>
              </tr>
            </tbody>
            <tfoot v-if="detail.product_items.length > 0">
              <tr class="total-row">
                <td colspan="6" class="text-right">JAMI:</td>
                <td class="num bold">{{ formatMoney(detail.summary.product_revenue) }}</td>
                <td class="num bold muted">{{ formatMoney(detail.summary.product_cost) }}</td>
                <td class="num bold">
                  <span :class="profitClass(detail.summary.product_profit)">
                    {{ formatMoney(detail.summary.product_profit) }}
                  </span>
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- SERVICES -->
        <div v-if="detail.services.total > 0" class="services-block">
          <h5 class="section-title">🔧 Xizmatlar</h5>
          <div class="services-grid">
            <div v-if="detail.services.labor_price_chakana > 0" class="svc-cell">
              <span class="svc-label">Ish haqi (chakana)</span>
              <span class="svc-val">{{ formatMoney(detail.services.labor_price_chakana) }}</span>
            </div>
            <div v-if="detail.services.labor_price_optom > 0" class="svc-cell">
              <span class="svc-label">Ish haqi (optom)</span>
              <span class="svc-val">{{ formatMoney(detail.services.labor_price_optom) }}</span>
            </div>
            <div v-if="detail.services.redo_price_chakana > 0" class="svc-cell">
              <span class="svc-label">Qayta ishlash (chakana)</span>
              <span class="svc-val">{{ formatMoney(detail.services.redo_price_chakana) }}</span>
            </div>
            <div v-if="detail.services.redo_price_optom > 0" class="svc-cell">
              <span class="svc-label">Qayta ishlash (optom)</span>
              <span class="svc-val">{{ formatMoney(detail.services.redo_price_optom) }}</span>
            </div>
            <div class="svc-cell total">
              <span class="svc-label">JAMI</span>
              <span class="svc-val">{{ formatMoney(detail.services.total) }}</span>
            </div>
          </div>
        </div>

        <!-- TO'LOV TURLARI — dinamik (pay_type jadvalidan keladi, har pay_type alohida) -->
        <div v-if="paymentsList.length > 0 || hasOldPaymentBreakdown" class="payments-block">
          <h5 class="section-title">💰 To'lov turlari</h5>
          <div class="payments-grid">
            <!-- Yangi: dinamik payments arrayi -->
            <div
              v-for="p in paymentsList"
              :key="p.id"
              class="pay-cell"
              :class="payCellClass(p.pay_type_name)"
            >
              <span class="pay-icon">{{ payIcon(p.pay_type_name) }}</span>
              <div class="pay-info">
                <span class="pay-label">{{ p.pay_type_name }}</span>
                <strong v-if="isDollarName(p.pay_type_name)">
                  {{ p.summa }} $
                  <small style="font-size:11px;font-weight:600;color:#94a3b8;display:block">
                    ≈ {{ formatMoney(p.summa_in_sum) }}
                  </small>
                </strong>
                <strong v-else>{{ formatMoney(p.summa) }}</strong>
              </div>
            </div>

            <!-- Eski savdolar uchun fallback (cash_summa va boshqalar) -->
            <template v-if="paymentsList.length === 0">
              <div v-if="Number(detail.sale.cash_summa) > 0" class="pay-cell pay-naqd">
                <span class="pay-icon">💵</span>
                <div class="pay-info">
                  <span class="pay-label">Naqd</span>
                  <strong>{{ formatMoney(detail.sale.cash_summa) }}</strong>
                </div>
              </div>
              <div v-if="Number(detail.sale.plastic_summa) > 0" class="pay-cell pay-plastic">
                <span class="pay-icon">💳</span>
                <div class="pay-info">
                  <span class="pay-label">Plastik</span>
                  <strong>{{ formatMoney(detail.sale.plastic_summa) }}</strong>
                </div>
              </div>
              <div v-if="Number(detail.sale.shot_summa) > 0" class="pay-cell pay-shot">
                <span class="pay-icon">📲</span>
                <div class="pay-info">
                  <span class="pay-label">Shot/Перевод</span>
                  <strong>{{ formatMoney(detail.sale.shot_summa) }}</strong>
                </div>
              </div>
              <div v-if="Number(detail.sale.dollar_summa) > 0" class="pay-cell pay-dollar">
                <span class="pay-icon">💲</span>
                <div class="pay-info">
                  <span class="pay-label">Dollar</span>
                  <strong>{{ Number(detail.sale.dollar_summa) }} $</strong>
                </div>
              </div>
            </template>

            <!-- Jami va qarz -->
            <div class="pay-cell pay-total">
              <span class="pay-icon">✅</span>
              <div class="pay-info">
                <span class="pay-label">JAMI to'langan</span>
                <strong>{{ formatMoney(detail.sale.paid_summa) }}</strong>
              </div>
            </div>
            <div v-if="Number(detail.sale.qarz_summa) > 0 || Number(detail.sale.qarz_dollar_summa) > 0" class="pay-cell pay-debt">
              <span class="pay-icon">⏳</span>
              <div class="pay-info">
                <span class="pay-label">QARZ</span>
                <strong>
                  <span v-if="Number(detail.sale.qarz_summa) > 0">{{ formatMoney(detail.sale.qarz_summa) }}</span>
                  <span v-if="Number(detail.sale.qarz_dollar_summa) > 0">
                    + {{ Number(detail.sale.qarz_dollar_summa) }} $
                  </span>
                </strong>
              </div>
            </div>
          </div>
        </div>

        <!-- BREAKDOWN -->
        <h5 class="section-title">💵 Hisob-kitob</h5>
        <div class="breakdown">
          <!-- ── NARX VA CHEGIRMALAR ── -->
          <div v-if="detail.summary.original_total > 0" class="bd-row">
            <span>Asl jami narx</span>
            <span>{{ formatMoney(detail.summary.original_total) }}</span>
          </div>
          <div v-if="detail.summary.arzon_discount > 0" class="bd-row negative">
            <span>− Narxdan tushirilgan (arzon)</span>
            <span>{{ formatMoney(detail.summary.arzon_discount) }}</span>
          </div>
          <div v-if="detail.summary.skidka_discount > 0" class="bd-row negative">
            <span>− Umumiy chegirma</span>
            <span>{{ formatMoney(detail.summary.skidka_discount) }}</span>
          </div>
          <div v-if="detail.summary.discount_total > 0" class="bd-row negative" style="font-weight:700">
            <span>− Jami chegirma</span>
            <span>{{ formatMoney(detail.summary.discount_total) }}</span>
          </div>
          <div v-if="detail.summary.final_total > 0" class="bd-row positive">
            <span><b>= Yakuniy narx (mijoz to'lagan)</b></span>
            <span><b>{{ formatMoney(detail.summary.final_total) }}</b></span>
          </div>

          <div class="bd-divider"></div>

          <!-- ── FOYDA HISOBI ── -->
          <div class="bd-row">
            <span>Mahsulot tushumi</span><span>{{ formatMoney(detail.summary.product_revenue) }}</span>
          </div>
          <div class="bd-row">
            <span>+ Xizmat tushumi</span><span>{{ formatMoney(detail.summary.service_revenue) }}</span>
          </div>
          <div class="bd-row positive">
            <span><b>Jami tushum</b></span>
            <span><b>{{ formatMoney(detail.summary.total_revenue) }}</b></span>
          </div>
          <div class="bd-row negative">
            <span>− Mahsulot tannarx</span><span>{{ formatMoney(detail.summary.product_cost) }}</span>
          </div>
          <div v-if="detail.summary.skidka_discount > 0" class="bd-row negative">
            <span>− Umumiy chegirma</span><span>{{ formatMoney(detail.summary.skidka_discount) }}</span>
          </div>
          <div class="bd-row total-row positive">
            <span><b>= Brutto foyda</b></span>
            <span><b>{{ formatMoney(detail.summary.gross_profit) }}</b></span>
          </div>
          <div v-if="detail.summary.kpi_share > 0" class="bd-row negative">
            <span>− KPI ulushi ({{ detail.user.percent }}%)</span>
            <span>{{ formatMoney(detail.summary.kpi_share) }}</span>
          </div>
          <div v-if="detail.summary.master_share > 0" class="bd-row negative">
            <span>− Usta ulushi</span>
            <span>{{ formatMoney(detail.summary.master_share) }}</span>
          </div>
          <div class="bd-row final" :class="detail.summary.net_profit >= 0 ? 'positive' : 'negative'">
            <span><b>= SOF FOYDA</b></span>
            <span><b>{{ formatMoney(detail.summary.net_profit) }}</b></span>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- ═══════ XODIM DRILL-DOWN: SAVDOLAR + MAHSULOT TAHLILI ═══════ -->
    <b-modal id="modal-employee" hide-footer size="xl" no-close-on-backdrop modal-class="modern-modal">
      <template #modal-title>
        <span v-if="empDetail">
          <b-icon icon="person-badge"></b-icon>
          {{ empDetail.first_name }} {{ empDetail.last_name }} — savdo va mahsulot tahlili
        </span>
      </template>

      <div v-if="empDetail" class="detail-content">
        <!-- SUMMARY CARDS (employees jadvalidan) -->
        <div class="detail-summary-row">
          <div class="d-card cyan">
            <span class="d-label">Savdolar</span>
            <span class="d-val">{{ empDetail.sales_count }} ta</span>
          </div>
          <div class="d-card blue">
            <span class="d-label">Tushum</span>
            <span class="d-val">{{ formatMoney(empDetail.total_revenue) }}</span>
          </div>
          <div class="d-card orange">
            <span class="d-label">Tannarx</span>
            <span class="d-val">{{ formatMoney(empDetail.product_cost) }}</span>
          </div>
          <div class="d-card green">
            <span class="d-label">Brutto foyda</span>
            <span class="d-val">{{ formatMoney(empDetail.gross_profit) }}</span>
          </div>
          <div class="d-card" :class="empDetail.net_profit >= 0 ? 'success-strong' : 'danger'">
            <span class="d-label">Sof foyda</span>
            <span class="d-val big">{{ formatMoney(empDetail.net_profit) }}</span>
          </div>
        </div>

        <!-- MUAMMO BELGILARI -->
        <div v-if="empLinesSummary" class="emp-flags">
          <span class="flag-pill loss" v-if="empLinesSummary.loss_lines">
            ⚠️ {{ empLinesSummary.loss_lines }} ta mahsulot zararga sotilgan ({{ formatMoney(empLinesSummary.loss_amount) }})
          </span>
          <span class="flag-pill low" v-if="empLinesSummary.low_lines">
            🔻 {{ empLinesSummary.low_lines }} ta arzon sotilgan (foyda &lt; 10%)
          </span>
          <span class="flag-pill inflated" v-if="empLinesSummary.inflated_lines">
            📈 {{ empLinesSummary.inflated_lines }} ta tannarx shishgan
          </span>
          <span class="flag-pill ok" v-if="!empLinesSummary.loss_lines && !empLinesSummary.low_lines && !empLinesSummary.inflated_lines">
            ✅ Muammoli mahsulot topilmadi
          </span>
        </div>

        <!-- TABS -->
        <div class="emp-tabs">
          <button class="emp-tab" :class="{ active: empTab === 'products' }" @click="empTab = 'products'">
            📦 Mahsulotlar tahlili
          </button>
          <button class="emp-tab" :class="{ active: empTab === 'sales' }" @click="empTab = 'sales'">
            🧾 Savdolar ({{ empSales.length }})
          </button>
        </div>

        <!-- ── PRODUCTS TAB ── -->
        <div v-show="empTab === 'products'">
          <div class="emp-search-row">
            <b-icon icon="search" class="emp-search-icon"></b-icon>
            <input type="text" v-model="empSearch" class="emp-search-input"
                   placeholder="Mahsulot nomi yoki raqami bo'yicha qidirish..." />
            <button v-if="empSearch" class="emp-search-clear" @click="empSearch = ''" title="Tozalash">✕</button>
          </div>
          <div class="emp-filter-row">
            <button class="chip" :class="{ active: empLineFilter === 'all' }" @click="empLineFilter = 'all'">
              Hammasi ({{ empLines.length }})
            </button>
            <button class="chip chip-loss" :class="{ active: empLineFilter === 'loss' }" @click="empLineFilter = 'loss'">
              Zarar ({{ countLineStatus('loss') }})
            </button>
            <button class="chip chip-low" :class="{ active: empLineFilter === 'low' }" @click="empLineFilter = 'low'">
              Arzon ({{ countLineStatus('low') }})
            </button>
            <button class="chip chip-inflated" :class="{ active: empLineFilter === 'inflated' }" @click="empLineFilter = 'inflated'">
              Tannarx shishgan ({{ empLinesSummary ? empLinesSummary.inflated_lines : 0 }})
            </button>
          </div>

          <div class="items-table-wrapper" style="max-height:440px">
            <table class="custom-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Mahsulot</th>
                  <th>Savdo</th>
                  <th class="num">Soni</th>
                  <th class="num">Tannarx (birlik)</th>
                  <th class="num">Sotilgan (birlik)</th>
                  <th class="num">Birlik foyda</th>
                  <th class="num">Jami foyda</th>
                  <th class="num">%</th>
                  <th>Holat</th>
                  <th style="width:60px;text-align:center">Amal</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(l, i) in filteredEmpLines" :key="l.line_id"
                    :class="lineRowClass(l)" style="cursor:pointer"
                    @click="openDetail({ sale_id: l.sale_id })" title="Savdoning to'liq tafsiloti">
                  <td>{{ i + 1 }}</td>
                  <td class="name-cell">
                    <div class="user-name">{{ l.product_name }}</div>
                    <div class="user-username">{{ l.extra_name }} · #{{ l.product_number }}</div>
                  </td>
                  <td class="muted">
                    #{{ l.number }}
                    <div class="user-username">{{ formatDate(l.created_at) }}</div>
                  </td>
                  <td class="num">{{ l.count }} {{ l.unit_name }}</td>
                  <td class="num">
                    <span :class="{ 'cost-hot': l.cost_inflated }">{{ formatMoney(l.unit_cost) }}</span>
                    <div v-if="l.cost_inflated" class="cost-warn">▲ {{ l.cost_inflated_pct }}% baland</div>
                  </td>
                  <td class="num">{{ formatMoney(l.sold_price) }}</td>
                  <td class="num">
                    <span :class="profitClass(l.unit_profit)">{{ formatMoney(l.unit_profit) }}</span>
                  </td>
                  <td class="num bold">
                    <span :class="profitClass(l.line_profit)">{{ formatMoney(l.line_profit) }}</span>
                  </td>
                  <td class="num">
                    <span :class="profitPctClass(l.profit_pct)">{{ l.profit_pct }}%</span>
                  </td>
                  <td>
                    <span class="status-badge" :class="lineStatusClass(l)">{{ lineStatusLabel(l) }}</span>
                  </td>
                  <td style="text-align:center" @click.stop>
                    <button class="fix-btn" @click.stop="openFixCost(l)" title="Tannarxni to'g'rilash">
                      <b-icon icon="pencil-square"></b-icon>
                    </button>
                  </td>
                </tr>
                <tr v-if="filteredEmpLines.length === 0 && !empLoading">
                  <td colspan="11" class="empty-row">
                    <span v-if="empSearch">«{{ empSearch }}» bo'yicha mahsulot topilmadi.</span>
                    <span v-else>Mahsulot topilmadi.</span>
                  </td>
                </tr>
                <tr v-if="empLoading">
                  <td colspan="11" class="loading-row"><div class="spinner"></div> Yuklanmoqda...</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="emp-hint">
            💡 Qatorga bossangiz — o'sha savdoning to'liq tafsiloti ochiladi.
            <b>Tannarx</b> ustunidagi qizil ▲ belgi — shu mahsulot odatdagidan qimmat tannarxda kiritilganini bildiradi.
          </p>
        </div>

        <!-- ── SALES TAB ── -->
        <div v-show="empTab === 'sales'">
          <div class="items-table-wrapper" style="max-height:440px">
            <table class="custom-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Sana</th>
                  <th>Mijoz</th>
                  <th class="num">Tushum</th>
                  <th class="num">Tannarx</th>
                  <th class="num">Foyda</th>
                  <th class="num">%</th>
                  <th class="num">Sof foyda</th>
                  <th>Status</th>
                  <th style="width:50px;text-align:center">To'liq</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(s, i) in empSales" :key="s.sale_id"
                    :class="rowProfitClass(s)" style="cursor:pointer"
                    @click="openDetail(s)" title="Savdoning to'liq tafsiloti">
                  <td>{{ i + 1 }}</td>
                  <td class="muted">{{ formatDate(s.created_at) }}</td>
                  <td>{{ s.kontragent ? s.kontragent.name : '—' }}</td>
                  <td class="num bold money">{{ formatMoney(s.revenue) }}</td>
                  <td class="num muted">{{ formatMoney(s.product_cost) }}</td>
                  <td class="num"><span :class="profitClass(s.gross_profit)">{{ formatMoney(s.gross_profit) }}</span></td>
                  <td class="num"><span :class="profitPctClass(s.profit_pct)">{{ s.profit_pct }}%</span></td>
                  <td class="num bold"><span :class="profitClass(s.net_profit)">{{ formatMoney(s.net_profit) }}</span></td>
                  <td><span class="status-badge" :class="'status-' + s.status">{{ statusLabel(s.status) }}</span></td>
                  <td style="text-align:center"><b-icon icon="eye-fill" style="color:#16a34a"></b-icon></td>
                </tr>
                <tr v-if="empSales.length === 0 && !empLoading">
                  <td colspan="10" class="empty-row">Savdolar topilmadi.</td>
                </tr>
                <tr v-if="empLoading">
                  <td colspan="10" class="loading-row"><div class="spinner"></div> Yuklanmoqda...</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- ═══════ TANNARXNI TO'G'RILASH MODAL ═══════ -->
    <b-modal id="modal-fix-cost" hide-footer no-close-on-backdrop modal-class="modern-modal"
             title="🛠 Tannarxni to'g'rilash">
      <div v-if="fixLoading" class="text-center" style="padding:30px">
        <b-spinner variant="primary"></b-spinner>
        <div class="mt-2 text-muted">Yuklanmoqda...</div>
      </div>

      <div v-else-if="fixCtx" class="fix-body">
        <!-- Mahsulot va savdo -->
        <div class="fix-head">
          <div class="fix-product">{{ fixCtx.product_name }}</div>
          <div class="fix-sub">{{ fixCtx.extra_name }} · #{{ fixCtx.product_number }}</div>
          <div class="fix-meta">
            Savdo #{{ fixCtx.sale_number }} · {{ formatDate(fixCtx.created_at) }} ·
            {{ fixCtx.count }} {{ fixCtx.unit_name }} · sotilgan {{ formatMoney(fixCtx.sold_unit_price) }}/birlik
          </div>
        </div>

        <!-- Hozirgi (xato) tannarx -->
        <div class="fix-current">
          <span class="fix-label">Hozirgi birlik tannarx</span>
          <span class="fix-current-val">{{ formatMoney(fixCtx.current_unit_cost_sum) }}</span>
          <span v-if="fixCtx.cost_currency === 'USD'" class="fix-raw">
            (xom: {{ fixCtx.current_body_price }} $ × kurs {{ formatMoney(fixCtx.dollar_rate) }})
          </span>
        </div>

        <!-- Yangi tannarx -->
        <div class="fix-input-block">
          <label>To'g'ri birlik tannarx</label>

          <!-- Kiritish valyutasi -->
          <div class="cur-row">
            <span class="cur-row-label">Kiritish:</span>
            <div class="cur-toggle">
              <button type="button" class="cur-btn" :class="{ active: fixInputCur === 1 }" @click="fixInputCur = 1">so'm</button>
              <button type="button" class="cur-btn" :class="{ active: fixInputCur === 2 }" @click="fixInputCur = 2">$ dollar</button>
            </div>
          </div>

          <input type="number" v-model.number="fixNewCost" class="form-input" min="0"
                 :placeholder="fixInputCur === 2 ? 'dollar (masalan 7.2)' : 'so\'m'" />

          <!-- Saqlash valyutasi -->
          <div class="cur-row">
            <span class="cur-row-label">Saqlash:</span>
            <div class="cur-toggle">
              <button type="button" class="cur-btn" :class="{ active: fixStoreCur === 1 }" @click="fixStoreCur = 1">so'm</button>
              <button type="button" class="cur-btn" :class="{ active: fixStoreCur === 2 }" @click="fixStoreCur = 2">$ dollar</button>
            </div>
          </div>

          <div v-if="fixNewCost > 0 && fixCtx.dollar_rate > 0" class="fix-hint-sum">
            → Saqlanadi: <b>{{ fixStoreCur === 2 ? (fixStoreRaw + ' $') : formatMoney(fixStoreRaw) }}</b>
            <span class="fix-hint-sub">≈ {{ formatMoney(fixNewUnitCostSum) }} (kurs {{ formatMoney(fixCtx.dollar_rate) }})</span>
          </div>
          <div v-if="fixScope === 'line' && fixStoreCur !== (fixCtx.cost_currency === 'USD' ? 2 : 1)" class="fix-hint-sub" style="color:#b45309">
            ⚠️ "Faqat shu qator"da partiya valyutasi o'zgarmaydi — qiymat to'g'ri (so'mda) saqlanadi. Valyutani butunlay almashtirish uchun "Butun partiya"ni tanlang.
          </div>
        </div>

        <!-- Yangi foyda preview -->
        <div v-if="fixNewCost !== '' && fixNewCost >= 0" class="fix-preview">
          <div class="fp-cell">
            <span class="fp-label">Yangi birlik foyda</span>
            <span class="fp-val" :class="profitClass(fixNewUnitProfit)">{{ formatMoney(fixNewUnitProfit) }}</span>
          </div>
          <div class="fp-cell">
            <span class="fp-label">Yangi foyda %</span>
            <span class="fp-val" :class="profitPctClass(fixNewProfitPct)">{{ fixNewProfitPct }}%</span>
          </div>
          <div class="fp-cell">
            <span class="fp-label">Jami foyda ({{ fixCtx.count }} {{ fixCtx.unit_name }})</span>
            <span class="fp-val" :class="profitClass(fixNewLineProfit)">{{ formatMoney(fixNewLineProfit) }}</span>
          </div>
        </div>

        <!-- Qamrov -->
        <div class="fix-scope">
          <label class="scope-opt" :class="{ active: fixScope === 'line' }">
            <input type="radio" value="line" v-model="fixScope" />
            <div>
              <b>Faqat shu savdo qatori</b>
              <small>Boshqa savdolarga ta'sir qilmaydi</small>
            </div>
          </label>
          <label class="scope-opt" :class="{ active: fixScope === 'series' }">
            <input type="radio" value="series" v-model="fixScope" />
            <div>
              <b>Butun partiya (series)</b>
              <small>
                Partiya tannarxi + shu partiyaga tegishli
                <b>{{ fixCtx.series_docs_count }}</b> ta savdo ({{ fixCtx.series_rows_count }} qator) tuzatiladi.
                Ombor bahosi ham yangilanadi.
              </small>
            </div>
          </label>
        </div>

        <div v-if="fixError" class="fix-error">⚠️ {{ fixError }}</div>
        <div v-if="fixResult" class="fix-done">
          ✅ Tuzatildi: {{ fixResult.updated_lines }} ta savdo qatori<span v-if="fixResult.updated_series"> + partiya tannarxi</span>. Hisobot yangilandi.
        </div>

        <div class="fix-actions">
          <button class="btn-secondary" @click="$bvModal.hide('modal-fix-cost')">Yopish</button>
          <button v-if="!fixResult" class="btn-primary"
                  :disabled="fixApplying || fixNewCost === '' || fixNewCost < 0"
                  @click="applyFixCost()">
            <b-icon :icon="fixApplying ? 'arrow-repeat' : 'check-all'"></b-icon>
            {{ fixApplying ? 'Saqlanmoqda...' : 'Tannarxni saqlash' }}
          </button>
        </div>
      </div>
    </b-modal>

    <!-- ═══════ SHUBHALI TANNARX (cost-anomalies) MODAL ═══════ -->
    <b-modal id="modal-anomalies" hide-footer size="xl" no-close-on-backdrop modal-class="modern-modal"
             title="⚠️ Shubhali / eng katta tannarxli qatorlar">
      <div class="detail-content">
        <div v-if="anomaliesSummary" class="emp-flags">
          <span class="flag-pill loss" v-if="anomaliesSummary.count">
            {{ anomaliesSummary.count }} ta shubhali qator (eng katta tannarxlilar)
          </span>
          <span class="flag-pill inflated" v-if="anomaliesSummary.dollar_suspect_count">
            📈 {{ anomaliesSummary.dollar_suspect_count }} ta dollar deb belgilangan (tannarx shishgan gumoni)
          </span>
          <span class="flag-pill ok" v-if="!anomaliesLoading && anomalies.length === 0">
            ✅ Shubhali tannarx topilmadi
          </span>
        </div>
        <!-- DAVR JAMISI — mobil Foyda-Zarar (profit-loss) tannarxiga MOS kelishi kerak -->
        <div v-if="anomaliesSummary && (anomaliesSummary.total_cost || anomaliesSummary.total_revenue)" class="anomaly-totals">
          <div class="at-cell"><span class="at-label">Davr — jami sotilgan</span><span class="at-val">{{ formatMoney(anomaliesSummary.total_revenue) }}</span></div>
          <div class="at-cell"><span class="at-label">Davr — jami tannarx</span><span class="at-val" :class="{ danger: anomaliesSummary.total_cost > anomaliesSummary.total_revenue }">{{ formatMoney(anomaliesSummary.total_cost) }}</span></div>
          <div class="at-cell"><span class="at-label">Davr — foyda/zarar</span><span class="at-val" :class="anomaliesSummary.total_profit >= 0 ? '' : 'danger'">{{ formatMoney(anomaliesSummary.total_profit) }}</span></div>
        </div>
        <p v-if="anomaliesSummary && anomaliesSummary.total_cost" class="emp-hint" style="border-left-color:#2563eb">
          ℹ️ "Davr — jami tannarx" mobil Foyda-Zarar (kunlik) TANNARX yig'indisiga mos kelishi kerak
          (service_sale + chiqim, dollar×joriy kurs). Mos kelsa — bir xil ma'lumot; eng katta tannarxli
          qator pastdagi ro'yxat tepasida.
        </p>
        <p class="emp-hint">
          💡 Savdo, chiqim VA <b>qaytarish (↩)</b> qatorlari — eng katta tannarx (magnitudada) tepada.
          Mobil Foyda-Zarar zararni ko'p ko'rsatsa, sababi ko'pincha <b>dollar deb noto'g'ri belgilangan
          mahsulotning QAYTARISHI</b> (tannarx ×kurs shishadi). ✏️ bilan tannarxni (yoki valyutani) to'g'rilang —
          eng yaxshisi <b>"Butun partiya"</b> (series) ni tanlang, shunda savdo ham, qaytarish ham tuzaladi.
        </p>

        <div class="items-table-wrapper" style="max-height:460px">
          <table class="custom-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Mahsulot</th>
                <th>Savdo / sana</th>
                <th class="num">Soni</th>
                <th class="num">Tannarx (birlik)</th>
                <th class="num">Sotilgan (birlik)</th>
                <th class="num">Jami foyda/zarar</th>
                <th class="num">%</th>
                <th>Amal</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(l, i) in anomalies" :key="l.line_id" :class="l.line_profit < 0 ? 'row-loss' : 'row-profit'">
                <td>{{ i + 1 }}</td>
                <td class="name-cell">
                  <div class="user-name">{{ l.product_name }}</div>
                  <div class="user-username">{{ l.extra_name }} · #{{ l.product_number }}</div>
                </td>
                <td class="muted">
                  <span v-if="l.is_refund" class="status-badge status-loss">↩ Qaytarish</span>
                  <span v-else-if="l.is_sale">#{{ l.sale_number }}</span>
                  <span v-else class="status-badge status-breakeven">Chiqim</span>
                  <div class="user-username">{{ formatDate(l.created_at) }}</div>
                  <div v-if="l.user_name" class="user-username">{{ l.user_name }}</div>
                </td>
                <td class="num">{{ l.count }} {{ l.unit_name }}</td>
                <td class="num">
                  <span :class="{ 'cost-hot': l.line_profit < 0 || l.dollar_suspect }">{{ formatMoney(l.unit_cost) }}</span>
                  <div v-if="l.cost_currency === 2" class="cost-warn">
                    ▲ {{ l.body_price }} $ × kurs{{ l.dollar_suspect ? ' ⚠' : '' }}
                  </div>
                </td>
                <td class="num">{{ formatMoney(l.sold_price) }}</td>
                <td class="num bold"><span :class="profitClass(l.line_profit)">{{ formatMoney(l.line_profit) }}</span></td>
                <td class="num"><span :class="profitClass(l.line_profit)">{{ l.profit_pct }}%</span></td>
                <td>
                  <button class="fix-btn" @click="openFixCost(l)" title="Tannarxni to'g'rilash">
                    <b-icon icon="pencil-square"></b-icon>
                  </button>
                </td>
              </tr>
              <tr v-if="anomalies.length === 0 && !anomaliesLoading">
                <td colspan="9" class="empty-row">Shubhali tannarx topilmadi ✓</td>
              </tr>
              <tr v-if="anomaliesLoading">
                <td colspan="9" class="loading-row"><div class="spinner"></div> Yuklanmoqda...</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-modal>

    <!-- ═══════ TANNARX 0 — FOYDANI TUZATISH MODAL ═══════ -->
    <b-modal id="modal-recalc" hide-footer no-close-on-backdrop modal-class="modern-modal" title="🛠 Tannarx 0 — foydani tuzatish">
      <div class="recalc-body">
        <div v-if="recalcLoading" class="text-center" style="padding:30px">
          <b-spinner variant="primary"></b-spinner>
          <div class="mt-2 text-muted">Tekshirilmoqda...</div>
        </div>

        <template v-else-if="recalcStat">
          <p class="recalc-info">
            Savdo paytida tannarx 0 bo'lgani uchun foyda shishib ketgan yozuvlar.
            Tannarxi endi tuzatilgan (partiya narxi > 0) qatorlar qayta hisoblanadi.
          </p>

          <div class="recalc-grid">
            <div class="rc-cell">
              <div class="rc-label">Tannarx 0 yozuvlar</div>
              <div class="rc-val">{{ recalcStat.zero_rows }}</div>
            </div>
            <div class="rc-cell ok">
              <div class="rc-label">Tuzatiladi</div>
              <div class="rc-val">{{ recalcStat.fixable_rows }}</div>
            </div>
            <div class="rc-cell warn">
              <div class="rc-label">Tuzatilmaydi (narx hali 0)</div>
              <div class="rc-val">{{ recalcStat.not_fixable_rows }}</div>
            </div>
            <div class="rc-cell">
              <div class="rc-label">Mahsulotlar</div>
              <div class="rc-val">{{ recalcStat.products_affected }}</div>
            </div>
            <div class="rc-cell">
              <div class="rc-label">Savdolar</div>
              <div class="rc-val">{{ recalcStat.docs_affected }}</div>
            </div>
            <div class="rc-cell danger">
              <div class="rc-label">Foyda taxminan kamayadi</div>
              <div class="rc-val">{{ formatMoney(recalcStat.estimated_profit_decrease) }}</div>
            </div>
          </div>

          <div v-if="recalcStat.not_fixable_rows > 0" class="recalc-note">
            ⚠️ {{ recalcStat.not_fixable_rows }} ta yozuv tuzatilmaydi — ularning sotilgan partiyasi
            (series) tannarxi hali 0. Avval o'sha mahsulotlar tannarxini to'g'rilang.
          </div>
          <div v-if="recalcResult" class="recalc-done">
            ✅ {{ recalcResult.updated }} ta yozuv tuzatildi. Hisobot yangilandi.
          </div>

          <div class="recalc-actions">
            <button class="btn-secondary" @click="$bvModal.hide('modal-recalc')">Yopish</button>
            <button v-if="!recalcResult" class="btn-danger-solid"
              :disabled="recalcApplying || recalcStat.fixable_rows === 0"
              @click="applyRecalc()">
              <b-icon :icon="recalcApplying ? 'arrow-repeat' : 'check-all'"></b-icon>
              {{ recalcApplying ? 'Tuzatilmoqda...' : `Tuzatish (${recalcStat.fixable_rows})` }}
            </button>
          </div>
        </template>
      </div>
    </b-modal>
  </div>
</template>

<script>
const LIMIT = 50;

export default {
  name: 'ProfitDetail',
  data() {
    return {
      LIMIT,
      mode: 'sales',
      loading: false,

      sklads: [],

      filters: {
        date1: this.firstDayOfMonth(),
        date2: this.today(),
        sklad_id: '',
        order_by: 'date',
      },

      sales: [],
      employees: [],
      pnl: null,            // Kunlik P&L (mobil profit-loss API bilan bir xil)
      total: 0,
      offset: 0,
      totals: null,

      detail: null,

      // Xodim drill-down (savdolar + mahsulot tahlili)
      empDetail: null,        // tanlangan xodim (employees jadvalidan)
      empTab: 'products',
      empLines: [],
      empLinesSummary: null,
      empSales: [],
      empLoading: false,
      empLineFilter: 'all',
      empSearch: '',

      // Tannarxni to'g'rilash
      fixCtx: null,
      fixNewCost: '',
      fixScope: 'line',
      fixInputCur: 1,        // kiritish valyutasi: 1=so'm, 2=dollar
      fixStoreCur: 1,        // saqlash valyutasi: 1=so'm, 2=dollar
      fixLoading: false,
      fixApplying: false,
      fixResult: null,
      fixError: '',

      // Shubhali tannarx (cost-anomalies)
      anomalies: [],
      anomaliesSummary: null,
      anomaliesLoading: false,

      // Tannarx 0 — foydani tuzatish
      recalcLoading: false,
      recalcApplying: false,
      recalcStat: null,
      recalcResult: null,
    };
  },
  computed: {
    hasMore() {
      return this.sales.length < this.total;
    },
    // Bugun/Hafta/Oy tugmalarining active holati — joriy sana oralig'idan
    // avtomatik aniqlanadi. Qo'lda sana tahrirlansa, hech biriga mos kelmay active o'chadi.
    activeRange() {
      const d1 = this.filters.date1;
      const d2 = this.filters.date2;
      const today = this.fmtLocal(new Date());
      if (d2 !== today) return null;
      if (d1 === today) return 'today';
      const wk = new Date(); wk.setDate(wk.getDate() - 7);
      if (d1 === this.fmtLocal(wk)) return 'week';
      if (d1 === this.firstDayOfMonth()) return 'month';
      return null;
    },
    // Backend dinamik payments[] qaytaradi
    paymentsList() {
      if (!this.detail || !Array.isArray(this.detail.payments)) return [];
      return this.detail.payments;
    },
    // Eski savdolar uchun fallback
    hasOldPaymentBreakdown() {
      if (!this.detail || !this.detail.sale) return false;
      const s = this.detail.sale;
      return Number(s.cash_summa || 0) > 0
          || Number(s.plastic_summa || 0) > 0
          || Number(s.shot_summa || 0) > 0
          || Number(s.dollar_summa || 0) > 0;
    },
    // Xodim mahsulot qatorlari — status filtri + nom/raqam bo'yicha qidiruv
    filteredEmpLines() {
      let list = this.empLines;
      if (this.empLineFilter === 'inflated') list = list.filter(l => l.cost_inflated);
      else if (this.empLineFilter !== 'all') list = list.filter(l => l.status === this.empLineFilter);
      const q = (this.empSearch || '').trim().toLowerCase();
      if (q) {
        list = list.filter(l =>
          String(l.product_name || '').toLowerCase().includes(q) ||
          String(l.extra_name || '').toLowerCase().includes(q) ||
          String(l.product_number || '').toLowerCase().includes(q)
        );
      }
      return list;
    },
    // Tannarx to'g'rilash — jonli preview
    // Kiritilgan qiymatning so'mdagi ekvivalenti (foyda hisobi shu — saqlashdan mustaqil)
    fixNewUnitCostSum() {
      if (!this.fixCtx) return 0;
      const rate = this.fixInputCur === 2 ? (this.fixCtx.dollar_rate || 1) : 1;
      return (parseFloat(this.fixNewCost) || 0) * rate;
    },
    // Bazaga saqlanadigan XOM qiymat (saqlash valyutasida)
    fixStoreRaw() {
      if (!this.fixCtx) return 0;
      if (this.fixStoreCur === 2) {
        const rate = this.fixCtx.dollar_rate || 0;
        return rate > 0 ? Math.round((this.fixNewUnitCostSum / rate) * 1000) / 1000 : 0;
      }
      return Math.round(this.fixNewUnitCostSum);
    },
    fixNewUnitProfit() {
      if (!this.fixCtx) return 0;
      return (this.fixCtx.sold_unit_price || 0) - this.fixNewUnitCostSum;
    },
    fixNewProfitPct() {
      const cost = this.fixNewUnitCostSum;
      if (cost <= 0) return 0;
      return Math.round((this.fixNewUnitProfit / cost) * 10000) / 100;
    },
    fixNewLineProfit() {
      if (!this.fixCtx) return 0;
      return this.fixNewUnitProfit * (this.fixCtx.count || 0);
    },
  },
  methods: {
    isDollarName(name) {
      const n = String(name || '').toLowerCase();
      return n.includes('dollar') || n.includes('usd') || n.includes('$') || n.startsWith('доллар');
    },
    payIcon(name) {
      const n = String(name || '').toLowerCase();
      if (this.isDollarName(name)) return '💲';
      if (n.includes('plast') || n.includes('пласт') || n.includes('card') || n.includes('uzcard') || n.includes('humo')) return '💳';
      if (n.includes('shot') || n.includes('шот') || n.includes('перевод')) return '📲';
      if (n.includes('naqd') || n.includes('нақд') || n.includes('cash') || n.includes('накд')) return '💵';
      return '💰';
    },
    payCellClass(name) {
      const n = String(name || '').toLowerCase();
      if (this.isDollarName(name)) return 'pay-dollar';
      if (n.includes('plast') || n.includes('uzcard') || n.includes('humo') || n.includes('card')) return 'pay-plastic';
      if (n.includes('shot') || n.includes('перевод')) return 'pay-shot';
      if (n.includes('naqd') || n.includes('cash')) return 'pay-naqd';
      return 'pay-other';
    },
    // Sanani MAHALLIY vaqt bo'yicha YYYY-MM-DD formatlaydi.
    // toISOString() UTC beradi — UTC+5'da tunda bir kun oldingi sanani ko'rsatib,
    // "Bugun" noto'g'ri ishlardi. fmtLocal kalendar kuniga aniq mos keladi.
    fmtLocal(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    today() {
      return this.fmtLocal(new Date());
    },
    firstDayOfMonth() {
      const d = new Date();
      d.setDate(1);
      return this.fmtLocal(d);
    },
    toUnix(s, end = false) {
      if (!s) return null;
      const d = new Date(s);
      if (end) d.setHours(23, 59, 59, 999);
      else d.setHours(0, 0, 0, 0);
      return Math.floor(d.getTime() / 1000);
    },
    formatMoney(n) {
      const v = parseFloat(n || 0);
      return v.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + " so'm";
    },
    formatDate(unix) {
      if (!unix) return '—';
      const d = new Date(unix * 1000);
      return d.toLocaleDateString('uz-UZ') + ' ' + d.toLocaleTimeString('uz-UZ', { hour: '2-digit', minute: '2-digit' });
    },
    profitClass(v) {
      const n = parseFloat(v || 0);
      if (n > 0) return 'positive';
      if (n < 0) return 'negative';
      return 'muted';
    },
    profitPctClass(p) {
      const n = parseFloat(p || 0);
      if (n >= 30) return 'positive';
      if (n >= 10) return 'pct-ok';
      if (n > 0)   return 'pct-warn';
      return 'negative';
    },
    rowProfitClass(r) {
      const profit = parseFloat(r.gross_profit || r.line_profit || 0);
      if (profit < 0) return 'row-loss';
      if (profit > 0) return 'row-profit';
      return '';
    },
    statusLabel(s) {
      return { profit: 'Foyda', loss: 'Zarar', breakeven: 'Tenglik' }[s] || s;
    },
    rankBadgeClass(rank) {
      if (rank === 1) return 'gold';
      if (rank === 2) return 'silver';
      if (rank === 3) return 'bronze';
      return 'normal';
    },

    // ════════ TANNARX 0 — FOYDANI TUZATISH ════════
    async openRecalc() {
      this.recalcStat = null;
      this.recalcResult = null;
      this.$bvModal.show('modal-recalc');
      this.recalcLoading = true;
      try {
        const res = await axios.get('api/v1/report/recalc-zero-cost-profit', { params: { mode: 'preview' } });
        this.recalcStat = res.data || null;
      } catch (e) {
        const msg = (e.response && e.response.data && (e.response.data.message || e.response.data.error)) || e.message;
        alert('Tekshirishda xatolik: ' + msg);
        this.$bvModal.hide('modal-recalc');
      } finally {
        this.recalcLoading = false;
      }
    },
    async applyRecalc() {
      if (!this.recalcStat || this.recalcStat.fixable_rows === 0) return;
      if (!confirm(`${this.recalcStat.fixable_rows} ta savdo yozuvining tannarxi va foydasi qayta hisoblanadi.\n\nBu tarixiy yozuvlarni o'zgartiradi. Davom etaylikmi?`)) return;
      this.recalcApplying = true;
      try {
        const res = await axios.get('api/v1/report/recalc-zero-cost-profit', { params: { mode: 'apply' } });
        this.recalcResult = res.data || null;
        await this.reload();
      } catch (e) {
        const msg = (e.response && e.response.data && (e.response.data.message || e.response.data.error)) || e.message;
        alert('Tuzatishda xatolik: ' + msg);
      } finally {
        this.recalcApplying = false;
      }
    },

    setMode(m) {
      this.mode = m;
      this.sales = [];
      this.employees = [];
      this.total = 0;
      this.offset = 0;
      this.totals = null;
      this.reload();
    },
    quickRange(t) {
      const now = new Date();
      const today = this.fmtLocal(now);
      if (t === 'today') {
        this.filters.date1 = today;
        this.filters.date2 = today;
      } else if (t === 'week') {
        const d = new Date(); d.setDate(d.getDate() - 7);
        this.filters.date1 = this.fmtLocal(d);
        this.filters.date2 = today;
      } else if (t === 'month') {
        this.filters.date1 = this.firstDayOfMonth();
        this.filters.date2 = today;
      }
      this.reload();
    },

    async reload() {
      this.offset = 0;
      this.sales = [];
      this.employees = [];
      this.pnl = null;
      this.totals = null;
      this.total = 0;
      if (this.mode === 'sales')         await this.loadSales();
      else if (this.mode === 'employee') await this.loadEmployees();
      else if (this.mode === 'pnl')      await this.loadPnl();
    },

    async loadPnl() {
      this.loading = true;
      try {
        const params = {
          date1: this.toUnix(this.filters.date1),
          date2: this.toUnix(this.filters.date2, true),
          granularity: 'day',
        };
        if (this.filters.sklad_id) params.sklad_id = this.filters.sklad_id;
        const res = await axios.get('api/v1/report/profit-loss', { params });
        this.pnl = res.data || null;
      } finally {
        this.loading = false;
      }
    },

    // Kunlik P&L jadvalida kunга bosilganda — shu kun bo'yicha Shubhali tannarx
    async openDayAnomalies(period) {
      if (period) { this.filters.date1 = period; this.filters.date2 = period; }
      await this.openAnomalies();
    },

    async loadSales() {
      this.loading = true;
      try {
        const params = {
          date1: this.toUnix(this.filters.date1),
          date2: this.toUnix(this.filters.date2, true),
          order_by: this.filters.order_by,
          limit: LIMIT,
          offset: 0,
        };
        if (this.filters.sklad_id) params.sklad_id = this.filters.sklad_id;
        const res = await axios.get('api/v1/report/sales-profit-list', { params });
        this.sales = res.data.items || [];
        this.total = res.data.total || 0;
        this.offset = this.sales.length;
        this.totals = res.data.totals || null;
      } finally {
        this.loading = false;
      }
    },

    async loadMore() {
      if (this.loading || !this.hasMore) return;
      this.loading = true;
      try {
        const params = {
          date1: this.toUnix(this.filters.date1),
          date2: this.toUnix(this.filters.date2, true),
          order_by: this.filters.order_by,
          limit: LIMIT,
          offset: this.sales.length,
        };
        if (this.filters.sklad_id) params.sklad_id = this.filters.sklad_id;
        const res = await axios.get('api/v1/report/sales-profit-list', { params });
        this.sales.push(...(res.data.items || []));
        this.offset = this.sales.length;
      } finally {
        this.loading = false;
      }
    },

    async loadEmployees() {
      this.loading = true;
      try {
        const params = {
          date1: this.toUnix(this.filters.date1),
          date2: this.toUnix(this.filters.date2, true),
        };
        if (this.filters.sklad_id) params.sklad_id = this.filters.sklad_id;
        const res = await axios.get('api/v1/report/profit-by-employee', { params });
        this.employees = res.data.items || [];
        this.totals = res.data.totals || null;
      } finally {
        this.loading = false;
      }
    },

    async openDetail(row) {
      this.detail = null;
      this.$bvModal.show('modal-detail');
      try {
        const res = await axios.get(`api/v1/report/sale-profit-detail/${row.sale_id}`);
        this.detail = res.data;
      } catch (e) {
        console.error(e);
      }
    },

    // ════════ SHUBHALI TANNARX ════════
    async openAnomalies() {
      this.anomalies = [];
      this.anomaliesSummary = null;
      this.$bvModal.show('modal-anomalies');
      this.anomaliesLoading = true;
      try {
        const params = {
          date1: this.toUnix(this.filters.date1),
          date2: this.toUnix(this.filters.date2, true),
          limit: 300,
        };
        if (this.filters.sklad_id) params.sklad_id = this.filters.sklad_id;
        const res = await axios.get('api/v1/report/cost-anomalies', { params });
        this.anomalies = res.data.lines || [];
        this.anomaliesSummary = res.data.summary || null;
      } catch (e) {
        console.error(e);
      } finally {
        this.anomaliesLoading = false;
      }
    },

    // ════════ XODIM DRILL-DOWN ════════
    countLineStatus(s) {
      return this.empLines.filter(l => l.status === s).length;
    },
    lineRowClass(l) {
      if (l.status === 'loss') return 'row-loss';
      if (l.status === 'low')  return 'row-warn';
      return '';
    },
    lineStatusClass(l) {
      if (l.status === 'loss') return 'status-loss';
      if (l.status === 'low')  return 'status-low';
      return 'status-profit';
    },
    lineStatusLabel(l) {
      if (l.status === 'loss') return 'Zarar';
      if (l.status === 'low')  return 'Arzon';
      return 'Yaxshi';
    },
    async openEmployee(row) {
      this.empDetail = row;
      this.empTab = 'products';
      this.empLineFilter = 'all';
      this.empSearch = '';
      this.empLines = [];
      this.empLinesSummary = null;
      this.empSales = [];
      this.$bvModal.show('modal-employee');
      this.empLoading = true;
      try {
        const params = {
          date1: this.toUnix(this.filters.date1),
          date2: this.toUnix(this.filters.date2, true),
          user_id: row.user_id,
        };
        if (this.filters.sklad_id) params.sklad_id = this.filters.sklad_id;
        const [linesRes, salesRes] = await Promise.all([
          axios.get('api/v1/report/employee-product-lines', { params }),
          axios.get('api/v1/report/sales-profit-list', {
            params: { ...params, order_by: 'profit', limit: 500, offset: 0 },
          }),
        ]);
        this.empLines = linesRes.data.lines || [];
        this.empLinesSummary = linesRes.data.summary || null;
        this.empSales = salesRes.data.items || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.empLoading = false;
      }
    },

    // ════════ TANNARXNI TO'G'RILASH ════════
    async openFixCost(line) {
      this.fixCtx = null;
      this.fixNewCost = '';
      this.fixScope = 'line';
      this.fixInputCur = 1;
      this.fixStoreCur = 1;
      this.fixResult = null;
      this.fixError = '';
      this.$bvModal.show('modal-fix-cost');
      this.fixLoading = true;
      try {
        const res = await axios.post('api/v1/report/fix-line-cost', {
          line_id: line.line_id,
          mode: 'preview',
        });
        this.fixCtx = res.data.line || null;
        // Tanlagichlarni mahsulotning joriy valyutasiga moslaymiz (1=so'm, 2=dollar)
        const cur = (this.fixCtx && this.fixCtx.cost_currency === 'USD') ? 2 : 1;
        this.fixInputCur = cur;
        this.fixStoreCur = cur;
      } catch (e) {
        this.fixError = this.errMsg(e);
      } finally {
        this.fixLoading = false;
      }
    },
    async applyFixCost() {
      if (!this.fixCtx || this.fixNewCost === '' || this.fixNewCost < 0) return;
      if (this.fixStoreCur === 2 && !(this.fixCtx.dollar_rate > 0)) {
        this.fixError = "Dollar kursi yo'q — dollarda saqlab bo'lmaydi";
        return;
      }
      const scopeText = this.fixScope === 'series'
        ? `Butun partiya — ${this.fixCtx.series_docs_count} ta savdo tannarxi`
        : 'Faqat shu savdo qatori';
      if (!confirm(`${scopeText} tannarxi ${this.formatMoney(this.fixNewUnitCostSum)} ga o'zgartiriladi.\n\nDavom etaylikmi?`)) return;
      this.fixApplying = true;
      this.fixError = '';
      try {
        const res = await axios.post('api/v1/report/fix-line-cost', {
          line_id: this.fixCtx.line_id,
          mode: 'apply',
          scope: this.fixScope,
          new_cost: this.fixStoreRaw,          // saqlash valyutasidagi xom qiymat
          cost_currency: this.fixStoreCur,     // 1=so'm, 2=dollar
        });
        this.fixResult = res.data || null;
        // Asosiy hisobotni yangilash (employees jadvali + totals)
        await this.reload();
        // Xodim drill-down'ni yangilangan agregatlar bilan qayta yuklash
        if (this.empDetail) {
          const updated = this.employees.find(e => e.user_id === this.empDetail.user_id);
          if (updated) this.empDetail = updated;
          await this.openEmployee(this.empDetail);
        }
        // Shubhali tannarx ro'yxatini ham yangilash (tuzatilgan qator chiqib ketsin)
        if (this.anomalies.length > 0) await this.openAnomalies();
      } catch (e) {
        this.fixError = this.errMsg(e);
      } finally {
        this.fixApplying = false;
      }
    },
    errMsg(e) {
      return (e.response && e.response.data && (e.response.data.message || e.response.data.error)) || e.message || 'Xatolik';
    },

    loadSklads() {
      axios.get('api/v1/sklads').then(r => { this.sklads = r.data || []; }).catch(() => {});
    },
  },
  mounted() {
    this.loadSklads();
    this.reload();
  },
};
</script>

<style scoped>
.g-page { padding: 20px; height: 100%; overflow-y: auto; background: #f8fafc; }
.content-container { max-width: 1500px; margin: 0 auto; }

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}
.page-title {
  display: flex; align-items: center; gap: 10px;
  font-size: 22px; font-weight: 800; color: #1e293b; margin: 0;
}
.title-icon { color: #16a34a; font-size: 24px; }

.header-actions { display: flex; gap: 6px; }
.mode-btn {
  background: white;
  border: 2px solid #e2e8f0;
  padding: 8px 18px;
  border-radius: 10px;
  font-weight: 700;
  color: #64748b;
  cursor: pointer;
  display: flex; align-items: center; gap: 7px;
  transition: 0.2s;
}
.mode-btn.active {
  border-color: #16a34a;
  color: #15803d;
  background: #f0fdf4;
  box-shadow: 0 2px 8px rgba(22,163,74,0.15);
}

/* FILTER */
.filter-panel {
  background: white;
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  padding: 16px;
  margin-bottom: 16px;
}
.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}
.filter-cell label {
  display: block; font-size: 11px; font-weight: 700;
  color: #94a3b8; margin-bottom: 6px;
  text-transform: uppercase; letter-spacing: 0.5px;
}
.form-input {
  width: 100%; background: #f8fafc;
  border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 9px 12px; font-size: 14px; color: #1e293b;
  outline: none; transition: 0.2s;
}
.form-input:focus { border-color: #16a34a; background: white; box-shadow: 0 0 0 3px rgba(22,163,74,0.08); }
.action-row { display: flex; gap: 6px; flex-wrap: wrap; }
.btn-primary {
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  color: white; border: none;
  padding: 9px 18px; border-radius: 10px;
  font-weight: 700; cursor: pointer;
  display: flex; align-items: center; gap: 6px;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(22,163,74,0.25);
}
.btn-primary:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; }
.btn-secondary {
  background: #f1f5f9; color: #334155; border: none;
  padding: 9px 14px; border-radius: 10px;
  font-weight: 600; font-size: 13px; cursor: pointer;
}
.btn-secondary:hover { background: #e2e8f0; }
.btn-secondary.active {
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(22,163,74,0.25);
}
.btn-secondary.active:hover { background: linear-gradient(135deg, #15803d 0%, #166534 100%); }

/* TANNARX 0 — FOYDANI TUZATISH */
.recalc-btn {
  background: #fff7ed; color: #c2410c; border: 2px solid #fed7aa;
  padding: 8px 16px; border-radius: 10px;
  font-weight: 700; cursor: pointer;
  display: flex; align-items: center; gap: 7px; transition: 0.2s;
}
.recalc-btn:hover { background: #ffedd5; border-color: #fdba74; }

.anomaly-btn {
  background: #fef2f2; color: #b91c1c; border: 2px solid #fecaca;
  padding: 8px 16px; border-radius: 10px;
  font-weight: 700; cursor: pointer;
  display: flex; align-items: center; gap: 7px; transition: 0.2s;
}
.anomaly-btn:hover { background: #fee2e2; border-color: #fca5a5; }

.pnl-note {
  font-size: 12px; color: #1e40af; background: #eff6ff; border-bottom: 1px solid #dbeafe;
  padding: 10px 16px; line-height: 1.5;
}
.pnl-note code { background: #dbeafe; padding: 1px 5px; border-radius: 4px; }

.anomaly-totals {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 12px;
}
.at-cell {
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 10px 14px; display: flex; flex-direction: column;
}
.at-label { font-size: 11px; color: #94a3b8; font-weight: 700; text-transform: uppercase; }
.at-val { font-size: 17px; font-weight: 800; color: #1e293b; margin-top: 3px; font-family: 'Inter', system-ui; }
.at-val.danger { color: #dc2626; }
.recalc-body { padding: 4px 2px; }
.recalc-info { color: #475569; font-size: 14px; line-height: 1.5; margin-bottom: 14px; }
.recalc-grid {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px; margin-bottom: 14px;
}
.rc-cell {
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px;
  padding: 12px 14px; border-left: 4px solid #94a3b8;
}
.rc-cell.ok     { border-left-color: #16a34a; background: #f0fdf4; }
.rc-cell.warn   { border-left-color: #f59e0b; background: #fffbeb; }
.rc-cell.danger { border-left-color: #dc2626; background: #fef2f2; }
.rc-label { font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.4px; }
.rc-val { font-size: 20px; font-weight: 800; color: #1e293b; margin-top: 4px; }
.recalc-note {
  background: #fffbeb; border: 1px solid #fde68a; border-radius: 10px;
  padding: 10px 14px; font-size: 13px; color: #92400e; margin-bottom: 12px;
}
.recalc-done {
  background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px;
  padding: 10px 14px; font-size: 14px; color: #15803d; font-weight: 700; margin-bottom: 12px;
}
.recalc-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 6px; }
.btn-danger-solid {
  background: linear-gradient(135deg, #ef4444, #dc2626); color: white; border: none;
  padding: 9px 18px; border-radius: 10px; font-weight: 700; cursor: pointer;
  display: flex; align-items: center; gap: 6px; font-size: 14px;
  box-shadow: 0 4px 12px rgba(220,38,38,0.25);
}
.btn-danger-solid:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; }

/* SUMMARY */
.summary-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px; margin-bottom: 14px;
}
.summary-card {
  background: white;
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border-left: 4px solid #94a3b8;
}
.summary-card.primary { border-color: #2563eb; }
.summary-card.success { border-color: #22c55e; }
.summary-card.success-strong { border-color: #16a34a; background: #f0fdf4; }
.summary-card.warning { border-color: #f59e0b; }
.summary-card.info    { border-color: #06b6d4; }
.summary-card.danger  { border-color: #ef4444; background: #fef2f2; }
.summary-card.purple  { border-color: #7c3aed; }
.card-label { font-size: 11px; color: #94a3b8; font-weight: 700; text-transform: uppercase; }
.card-value { font-size: 19px; font-weight: 800; color: #1e293b; margin: 4px 0 2px 0; font-family: 'Inter', system-ui; }
.card-sub   { font-size: 11px; color: #64748b; }

/* TABLE */
.table-card {
  background: white;
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  overflow: hidden;
}
.table-wrapper {
  overflow-x: auto;
  max-height: calc(100vh - 380px);
  overflow-y: auto;
}
.custom-table { width: 100%; border-collapse: collapse; }
.custom-table thead { position: sticky; top: 0; z-index: 2; }
.custom-table th {
  background: #f8fafc;
  padding: 11px 12px;
  color: #94a3b8; font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.5px;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap; text-align: left;
}
.custom-table th.num,
.custom-table td.num { text-align: right; }
.custom-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f8fafc;
  font-size: 13px; color: #334155;
  vertical-align: middle;
}
.custom-table tr.row-profit:hover td { background: #f0fdf4; }
.custom-table tr.row-loss td   { background: #fef2f2; }
.custom-table tr.row-loss:hover td { background: #fee2e2; }

.bold { font-weight: 800; color: #1e293b; }
.money { font-family: 'Inter', system-ui; }
.muted { color: #94a3b8; }

.name-cell .user-name { font-weight: 700; color: #1e293b; font-size: 13px; }
.name-cell .user-username { font-size: 11px; color: #94a3b8; margin-top: 2px; }

.kpi-cell    { color: #2563eb; font-weight: 700; }
.master-cell { color: #7c3aed; font-weight: 700; }

.positive { color: #16a34a; font-weight: 700; }
.negative { color: #dc2626; font-weight: 700; }
.pct-ok   { color: #475569; font-weight: 700; }
.pct-warn { color: #b45309; font-weight: 700; }

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}
.status-profit    { background: #dcfce7; color: #15803d; }
.status-loss      { background: #fee2e2; color: #b91c1c; }
.status-breakeven { background: #f1f5f9; color: #64748b; }

.rank-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 32px; padding: 4px 10px;
  border-radius: 999px;
  font-weight: 800; font-size: 13px;
  background: #f1f5f9; color: #64748b;
}
.rank-badge.gold   { background: linear-gradient(135deg, #fde047, #f59e0b); color: white; }
.rank-badge.silver { background: linear-gradient(135deg, #e2e8f0, #94a3b8); color: white; }
.rank-badge.bronze { background: linear-gradient(135deg, #fdba74, #c2410c); color: white; }

.kpi-pct  { background: #f0fdf4; color: #15803d; padding: 3px 10px; border-radius: 12px; font-size: 12px; font-weight: 700; }
.count-badge { background: #eff6ff; color: #1d4ed8; padding: 3px 10px; border-radius: 12px; font-size: 12px; font-weight: 700; }

.icon-btn {
  background: #f1f5f9; border: none;
  width: 32px; height: 32px; border-radius: 8px;
  cursor: pointer; color: #16a34a;
}
.icon-btn:hover { background: #16a34a; color: white; }

.empty-row { text-align: center; padding: 50px !important; color: #94a3b8; font-style: italic; }
.loading-row { text-align: center; padding: 30px; color: #16a34a; }

.spinner {
  display: inline-block; width: 16px; height: 16px;
  border: 2px solid #dcfce7; border-top-color: #16a34a;
  border-radius: 50%; animation: spin 0.7s linear infinite;
  vertical-align: middle; margin-right: 6px;
}
@keyframes spin { to { transform: rotate(360deg); } }

.pagination-bar {
  display: flex; align-items: center; gap: 14px;
  padding: 12px 18px; border-top: 1px solid #f1f5f9;
  font-size: 13px; color: #64748b;
}
.load-more-btn {
  background: #f0fdf4; color: #15803d; border: none;
  padding: 6px 16px; border-radius: 8px;
  font-weight: 700; font-size: 13px; cursor: pointer;
}
.load-more-btn:hover { background: #dcfce7; }
.all-loaded { color: #16a34a; font-weight: 700; }

/* DETAIL MODAL */
.detail-content { padding: 5px; }

.info-strip {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
  background: #f8fafc;
  padding: 10px 14px;
  border-radius: 10px;
  margin-bottom: 14px;
}
.info-cell { display: flex; flex-direction: column; }
.info-label { font-size: 10px; color: #94a3b8; font-weight: 700; text-transform: uppercase; }
.info-val   { font-size: 13px; color: #1e293b; font-weight: 700; margin-top: 2px; }

.detail-summary-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 8px;
  margin-bottom: 18px;
}
.d-card {
  background: white;
  border-radius: 10px;
  padding: 12px 14px;
  border-left: 4px solid #cbd5e1;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  display: flex; flex-direction: column;
}
.d-card.cyan   { border-color: #06b6d4; }
.d-card.orange { border-color: #f59e0b; }
.d-card.purple { border-color: #7c3aed; }
.d-card.green  { border-color: #22c55e; }
.d-card.blue   { border-color: #2563eb; }
.d-card.pink   { border-color: #ec4899; }
.d-card.success-strong { border-color: #16a34a; background: #f0fdf4; }
.d-card.danger { border-color: #dc2626; background: #fef2f2; }
.d-label { font-size: 10px; color: #94a3b8; font-weight: 700; text-transform: uppercase; }
.d-val   { font-size: 16px; font-weight: 800; color: #1e293b; margin-top: 4px; font-family: 'Inter', system-ui; }
.d-val.big { font-size: 19px; }

.section-title {
  font-size: 14px; font-weight: 800;
  color: #334155; margin: 14px 0 10px 0;
}

.items-table-wrapper {
  border: 1px solid #f1f5f9; border-radius: 10px;
  max-height: 300px; overflow-y: auto;
}

.total-row td {
  background: #f8fafc !important;
  font-weight: 800;
  border-top: 2px solid #e2e8f0;
}

.text-right { text-align: right; }

/* PAYMENTS BLOCK */
.payments-block { margin: 18px 0; }

.payments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px;
}

.pay-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 14px;
  transition: 0.2s;
}

.pay-cell:hover { transform: translateY(-1px); box-shadow: 0 4px 8px rgba(0,0,0,0.05); }

.pay-cell.pay-naqd    { background: #f0fdf4; border-color: #bbf7d0; }
.pay-cell.pay-plastic { background: #eff6ff; border-color: #bfdbfe; }
.pay-cell.pay-shot    { background: #fffbeb; border-color: #fde68a; }
.pay-cell.pay-dollar  { background: #ecfdf5; border-color: #6ee7b7; }
.pay-cell.pay-total   { background: linear-gradient(135deg, #ddd6fe 0%, #c4b5fd 100%); border-color: #a78bfa; }
.pay-cell.pay-debt    { background: #fef2f2; border-color: #fecaca; }
.pay-cell.pay-other   { background: #f8fafc; border-color: #cbd5e1; }

.pay-icon { font-size: 22px; }

.pay-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.pay-label {
  font-size: 10px;
  color: #94a3b8;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.pay-info strong {
  font-size: 16px;
  font-weight: 800;
  color: #1e293b;
  font-family: 'Inter', system-ui, sans-serif;
  margin-top: 2px;
}

.pay-cell.pay-total .pay-info strong { color: #5b21b6; }
.pay-cell.pay-debt .pay-info strong  { color: #b91c1c; }

.services-block { margin: 18px 0; }
.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px;
}
.svc-cell {
  background: #faf5ff; border: 1px solid #e9d5ff;
  border-radius: 10px; padding: 10px 14px;
  display: flex; flex-direction: column;
}
.svc-cell.total { background: #ede9fe; border-color: #c4b5fd; }
.svc-label { font-size: 11px; color: #7c3aed; font-weight: 700; text-transform: uppercase; }
.svc-val { font-size: 15px; font-weight: 800; color: #1e293b; margin-top: 4px; font-family: 'Inter', system-ui; }

.breakdown {
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px 20px;
  margin-top: 14px;
}
.bd-row {
  display: flex; justify-content: space-between;
  padding: 6px 0;
  font-size: 14px; color: #475569;
}
.bd-row.positive { color: #15803d; }
.bd-row.negative { color: #b91c1c; }
.bd-divider { border-top: 2px dashed #e2e8f0; margin: 8px 0; }
.bd-row.total-row {
  border-top: 1px solid #cbd5e1;
  padding-top: 10px; margin-top: 4px;
  font-size: 15px;
}
.bd-row.final {
  border-top: 2px solid #16a34a;
  padding-top: 12px; margin-top: 6px;
  font-size: 17px; font-weight: 800;
}
.bd-row.final.negative { border-color: #dc2626; }

/* ══════ XODIM DRILL-DOWN ══════ */
.clickable-row { cursor: pointer; transition: background 0.15s; }
.clickable-row:hover td { background: #f0f9ff; }

.status-low { background: #fef3c7; color: #b45309; }

.custom-table tr.row-warn td { background: #fffbeb; }
.custom-table tr.row-warn:hover td { background: #fef3c7; }

/* Muammo belgilari */
.emp-flags {
  display: flex; flex-wrap: wrap; gap: 8px;
  margin: 4px 0 14px 0;
}
.flag-pill {
  padding: 6px 12px; border-radius: 999px;
  font-size: 12px; font-weight: 700;
  border: 1px solid transparent;
}
.flag-pill.loss     { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
.flag-pill.low      { background: #fffbeb; color: #b45309; border-color: #fde68a; }
.flag-pill.inflated { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }
.flag-pill.ok       { background: #f0fdf4; color: #15803d; border-color: #bbf7d0; }

/* Tablar */
.emp-tabs { display: flex; gap: 6px; margin-bottom: 12px; border-bottom: 2px solid #f1f5f9; }
.emp-tab {
  background: transparent; border: none;
  padding: 9px 16px; font-weight: 700; font-size: 14px;
  color: #64748b; cursor: pointer;
  border-bottom: 3px solid transparent; margin-bottom: -2px;
}
.emp-tab.active { color: #15803d; border-bottom-color: #16a34a; }

/* Qidiruv */
.emp-search-row {
  display: flex; align-items: center; gap: 8px;
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 8px 12px; margin-bottom: 10px;
}
.emp-search-icon { color: #94a3b8; flex-shrink: 0; }
.emp-search-input {
  flex: 1; border: none; background: transparent; outline: none;
  font-size: 14px; color: #1e293b;
}
.emp-search-input::placeholder { color: #94a3b8; }
.emp-search-clear {
  border: none; background: #e2e8f0; color: #475569;
  width: 22px; height: 22px; border-radius: 50%;
  cursor: pointer; font-size: 12px; line-height: 1; flex-shrink: 0;
}
.emp-search-clear:hover { background: #cbd5e1; }

/* Filter chiplar */
.emp-filter-row { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
.chip {
  background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0;
  padding: 5px 12px; border-radius: 999px;
  font-size: 12px; font-weight: 700; cursor: pointer; transition: 0.15s;
}
.chip:hover { background: #e2e8f0; }
.chip.active { background: #1e293b; color: white; border-color: #1e293b; }
.chip.chip-loss.active     { background: #dc2626; border-color: #dc2626; }
.chip.chip-low.active      { background: #d97706; border-color: #d97706; }
.chip.chip-inflated.active { background: #2563eb; border-color: #2563eb; }

/* Tannarx shishgan belgisi */
.cost-hot { color: #dc2626; font-weight: 800; }
.cost-warn { font-size: 10px; color: #dc2626; font-weight: 700; margin-top: 2px; }

.emp-hint {
  font-size: 12px; color: #64748b; margin-top: 10px;
  background: #f8fafc; padding: 10px 14px; border-radius: 10px;
  border-left: 3px solid #cbd5e1;
}

/* ══════ TANNARXNI TO'G'RILASH ══════ */
.fix-btn {
  background: #fff7ed; border: 1px solid #fed7aa;
  width: 30px; height: 30px; border-radius: 8px;
  cursor: pointer; color: #c2410c;
}
.fix-btn:hover { background: #c2410c; color: white; border-color: #c2410c; }

.fix-body { padding: 4px 2px; }
.fix-head { margin-bottom: 12px; }
.fix-product { font-size: 16px; font-weight: 800; color: #1e293b; }
.fix-sub { font-size: 12px; color: #94a3b8; margin-top: 2px; }
.fix-meta { font-size: 12px; color: #64748b; margin-top: 6px; }

.fix-current {
  background: #fef2f2; border: 1px solid #fecaca; border-radius: 10px;
  padding: 12px 14px; margin-bottom: 14px;
  display: flex; flex-direction: column; gap: 2px;
}
.fix-label { font-size: 11px; font-weight: 700; color: #b91c1c; text-transform: uppercase; letter-spacing: 0.4px; }
.fix-current-val { font-size: 20px; font-weight: 800; color: #b91c1c; font-family: 'Inter', system-ui; }
.fix-raw { font-size: 11px; color: #94a3b8; }

.fix-input-block { margin-bottom: 14px; }
.fix-input-block label {
  display: block; font-size: 11px; font-weight: 700; color: #94a3b8;
  margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;
}
.fix-hint-sum { font-size: 12px; color: #15803d; font-weight: 700; margin-top: 6px; }
.fix-hint-sub { display: block; font-size: 11px; color: #94a3b8; font-weight: 600; margin-top: 2px; }
.cur-row { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.cur-row-label { font-size: 12px; font-weight: 700; color: #64748b; min-width: 62px; }
.cur-toggle { display: inline-flex; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; }
.cur-btn { border: none; background: #f8fafc; color: #64748b; padding: 7px 18px; font-weight: 700; font-size: 13px; cursor: pointer; transition: .15s; }
.cur-btn + .cur-btn { border-left: 1px solid #e2e8f0; }
.cur-btn.active { background: #2563eb; color: #fff; }

.fix-preview {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px;
  margin-bottom: 14px;
}
.fp-cell {
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 10px 12px; display: flex; flex-direction: column;
}
.fp-label { font-size: 10px; color: #94a3b8; font-weight: 700; text-transform: uppercase; }
.fp-val { font-size: 15px; font-weight: 800; margin-top: 4px; font-family: 'Inter', system-ui; }

.fix-scope { display: flex; flex-direction: column; gap: 8px; margin-bottom: 14px; }
.scope-opt {
  display: flex; align-items: flex-start; gap: 10px;
  background: #f8fafc; border: 2px solid #e2e8f0; border-radius: 10px;
  padding: 10px 14px; cursor: pointer; margin: 0;
}
.scope-opt.active { border-color: #16a34a; background: #f0fdf4; }
.scope-opt input { margin-top: 3px; }
.scope-opt b { font-size: 14px; color: #1e293b; display: block; }
.scope-opt small { font-size: 12px; color: #64748b; }

.fix-error {
  background: #fef2f2; border: 1px solid #fecaca; border-radius: 10px;
  padding: 10px 14px; font-size: 13px; color: #b91c1c; margin-bottom: 12px; font-weight: 600;
}
.fix-done {
  background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px;
  padding: 10px 14px; font-size: 14px; color: #15803d; font-weight: 700; margin-bottom: 12px;
}
.fix-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 6px; }
</style>
